import { Activity, Flame, Target, Goal as GoalIcon } from "lucide-react";

/**
 * Live In-Play Predictions panel — surfaces real-time momentum,
 * pressure, next-team-to-score, and another-goal forecasts when a
 * match is live.  Self-hides when in_play is missing.
 *
 * Props:
 *   inPlay: payload from /api/live/match/{fid}.in_play
 *   homeName, awayName: strings (from the match payload)
 */
export const LiveInPlayPanel = ({ inPlay, homeName, awayName }) => {
  if (!inPlay) return null;

  const score = `${inPlay.score_home}-${inPlay.score_away}`;
  const minute = inPlay.minute || inPlay.minutes_played;
  const statsAvail = !!inPlay.stats_available;

  const m = inPlay.momentum || {};
  const p = inPlay.pressure || {};
  const ng = inPlay.next_goal || {};
  const ag = inPlay.another_goal || {};
  const home = inPlay.home_stats || {};
  const away = inPlay.away_stats || {};

  // Momentum bar: differential is in [-1, +1], map to [0, 100]% home share
  const homeShare = Math.round(50 + (m.differential || 0) * 50);

  const StatRow = ({ label, hVal, aVal, accent }) => {
    const total = (Number(hVal) || 0) + (Number(aVal) || 0);
    const hPct = total > 0 ? (Number(hVal) / total) * 100 : 50;
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px] font-bold tracking-[0.06em] uppercase text-muted-foreground">
          <span className="font-tabular text-foreground tabular-nums">{hVal}</span>
          <span>{label}</span>
          <span className="font-tabular text-foreground tabular-nums">{aVal}</span>
        </div>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden flex">
          <div
            className={`${accent || "bg-emerald-500"} h-full transition-all duration-500`}
            style={{ width: `${hPct}%` }}
          />
          <div className="flex-1 bg-rose-400/80" />
        </div>
      </div>
    );
  };

  const PickPill = ({ label, value, percent, tone = "amber" }) => {
    const toneCls = {
      amber: "border-amber-400/40 bg-amber-400/10 text-amber-700 dark:text-amber-400",
      emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      rose: "border-rose-500/40 bg-rose-500/10 text-rose-500 dark:text-rose-400",
      slate: "border-slate-500/40 bg-slate-500/10 text-slate-500",
    }[tone];
    return (
      <div className={`rounded-xl border px-3 py-2.5 ${toneCls}`}>
        <div className="text-[10px] font-bold tracking-[0.08em] uppercase opacity-80">{label}</div>
        <div className="font-display font-bold text-base mt-0.5">{value}</div>
        <div className="text-[11px] font-tabular mt-0.5 tabular-nums">{percent}%</div>
      </div>
    );
  };

  const nextTone =
    ng.pick === "Home" ? "emerald" : ng.pick === "Away" ? "rose" : "slate";
  const anotherTone = ag.pick === "Yes" ? "amber" : "slate";

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.06] via-background to-amber-500/[0.06] p-5 sm:p-6 mb-8"
      data-testid="live-in-play-panel"
    >
      <div className="pointer-events-none absolute -top-16 -left-16 h-48 w-48 rounded-full bg-rose-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-amber-400/15 blur-3xl" />

      {/* Header */}
      <div className="relative flex flex-wrap items-baseline justify-between gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.16em] uppercase text-rose-500 mb-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
            </span>
            Live · in-play signals
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight">
            {homeName} {score} {awayName}{" "}
            <span className="text-muted-foreground font-normal text-base">· {minute}&apos;</span>
          </h3>
          {!statsAvail && (
            <p className="mt-1 text-[11px] text-muted-foreground italic">
              Pace-only model — full per-team stats not published by upstream for this fixture.
            </p>
          )}
        </div>
      </div>

      {/* Two-column: Momentum/Stats left, Predictions right */}
      <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left: momentum + stats */}
        <div className="lg:col-span-3 space-y-4">
          {/* Momentum bar */}
          <div className="rounded-xl bg-card/60 backdrop-blur-sm border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold tracking-[0.08em] uppercase text-muted-foreground inline-flex items-center gap-1.5">
                <Activity size={12} />
                Momentum
              </span>
              <span className="text-xs font-semibold">
                {m.leader === "Balanced" ? "Balanced" : `${m.leader} pushing`}
              </span>
            </div>
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden flex" data-testid="momentum-bar">
              <div
                className="h-full bg-emerald-500 transition-all duration-700"
                style={{ width: `${homeShare}%` }}
              />
              <div className="flex-1 bg-rose-400" />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
              <span className="font-semibold text-emerald-600 dark:text-emerald-400 truncate max-w-[40%]">
                {homeName} {Math.round((m.home || 0) * 100)}
              </span>
              <span className="font-semibold text-rose-500 truncate max-w-[40%] text-right">
                {Math.round((m.away || 0) * 100)} {awayName}
              </span>
            </div>
          </div>

          {/* Pressure gauges */}
          <div className="grid grid-cols-2 gap-3">
            <PressureGauge label={`${homeName} pressure`} value={p.home || 0} side="home" />
            <PressureGauge label={`${awayName} pressure`} value={p.away || 0} side="away" />
          </div>

          {/* Stat rows */}
          <div className="rounded-xl bg-card/60 backdrop-blur-sm border border-border p-4 space-y-3.5" data-testid="live-stats-grid">
            {inPlay.stats_available === false ? (
              <div className="text-xs text-muted-foreground italic text-center py-3">
                Live stats are still being aggregated by the data provider.
                Refresh in ~30s for updated shots / possession.
              </div>
            ) : (
              <>
                <StatRow label="Shots on target" hVal={home.shots_on_target} aVal={away.shots_on_target} accent="bg-emerald-500" />
                <StatRow label="Shots off target" hVal={home.shots_off_target} aVal={away.shots_off_target} accent="bg-emerald-400" />
                <StatRow label="Total shots" hVal={home.total_shots} aVal={away.total_shots} accent="bg-emerald-500" />
                {(home.dangerous_attacks > 0 || away.dangerous_attacks > 0) && (
                  <StatRow label="Dangerous attacks" hVal={home.dangerous_attacks} aVal={away.dangerous_attacks} accent="bg-amber-400" />
                )}
                <StatRow label="Possession %" hVal={home.ball_possession} aVal={away.ball_possession} accent="bg-blue-500" />
                <StatRow label="Corners" hVal={home.corner_kicks} aVal={away.corner_kicks} accent="bg-purple-500" />
              </>
            )}
          </div>
        </div>

        {/* Right: predictions */}
        <div className="lg:col-span-2 space-y-3">
          {/* Next goal split */}
          <div className="rounded-xl bg-card border border-border p-4" data-testid="next-goal-card">
            <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-[0.08em] uppercase text-muted-foreground mb-3">
              <Target size={12} />
              Next team to score
            </div>
            <div className="space-y-2">
              <NextGoalRow label={homeName} pct={ng.home_pct} tone="emerald" />
              <NextGoalRow label={awayName} pct={ng.away_pct} tone="rose" />
              <NextGoalRow label="No further goal" pct={ng.no_goal_pct} tone="slate" />
            </div>
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
              <span className="text-[11px] font-bold tracking-[0.08em] uppercase text-muted-foreground">
                Pick
              </span>
              <span className="font-display font-bold text-sm">
                {ng.pick} <span className="text-muted-foreground">· {ng.confidence}%</span>
              </span>
            </div>
          </div>

          {/* Another goal coming? */}
          <div className="rounded-xl bg-card border border-border p-4" data-testid="another-goal-card">
            <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-[0.08em] uppercase text-muted-foreground mb-3">
              <GoalIcon size={12} />
              Another goal coming?
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <PickPill label="Yes" value={`${ag.yes_pct}%`} percent="" tone={ag.pick === "Yes" ? "amber" : "slate"} />
              <PickPill label="No" value={`${ag.no_pct}%`} percent="" tone={ag.pick === "No" ? "amber" : "slate"} />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Projected FT</span>
              <span className="font-display font-bold font-tabular">
                {ag.projected_total} goals
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Over {ag.line_over_under}</span>
              <span className="font-tabular text-amber-700 dark:text-amber-400 font-semibold">
                {ag.over_line_yes_pct}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PressureGauge = ({ label, value, side }) => {
  const pct = Math.min(Math.max(Math.round((value || 0) * 100), 0), 100);
  const tone = side === "home" ? "from-emerald-500 to-emerald-400" : "from-rose-500 to-rose-400";
  return (
    <div className="rounded-xl bg-card/60 backdrop-blur-sm border border-border p-3" data-testid={`pressure-${side}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.06em] uppercase text-muted-foreground mb-1.5 truncate">
        <Flame size={10} />
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5 mb-1.5">
        <span className="font-display font-bold text-2xl font-tabular">{pct}</span>
        <span className="text-xs text-muted-foreground">/ 100</span>
      </div>
      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${tone} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const NextGoalRow = ({ label, pct, tone }) => {
  const toneCls = {
    emerald: "bg-emerald-500",
    rose: "bg-rose-500",
    slate: "bg-slate-400",
  }[tone];
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold truncate max-w-[70%]">{label}</span>
        <span className="font-tabular tabular-nums font-bold">{pct}%</span>
      </div>
      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${toneCls} transition-all duration-500`}
          style={{ width: `${Math.min(Math.max(pct || 0, 0), 100)}%` }}
        />
      </div>
    </div>
  );
};

export default LiveInPlayPanel;
